import { cloudConnectorAwsApi } from 'packages/tackle-api';
import { useQuery } from '@tanstack/react-query';

interface AwsCredentialsStatusAPIResponse {
  marketplace_sdds_bucket_arn: string | null;
  marketplace_sdds_key_arn: string | null;
  marketplace_sdds_topic_arn: string | null;
  last_assume_role_attempt: string | null;
  last_assume_role_status: string | null;
  last_assume_role_success: string | null;
  last_assume_role_error: string | null;
}

export interface AwsCredentialsState {
  s3Arn: string | null;
  kmsKeyArn: string | null;
  snsTopicArn: string | null;
  lastAssumeRoleStatus: string | null;
  lastAssumeRoleError: string | null;
}

export const getAwsCredentialsStatus =
  async (): Promise<AwsCredentialsState> => {
    try {
      const response = await cloudConnectorAwsApi
        .get('sources/credentials/status')
        .json<AwsCredentialsStatusAPIResponse>();
      return {
        s3Arn: response.marketplace_sdds_bucket_arn,
        kmsKeyArn: response.marketplace_sdds_key_arn,
        snsTopicArn: response.marketplace_sdds_topic_arn,
        lastAssumeRoleStatus: response.last_assume_role_status,
        lastAssumeRoleError: response.last_assume_role_error,
      };
    } catch (error) {
      // Log error but let React Query handle it
      console.error('Credentials status API error:', error);
      throw error;
    }
  };

/**
 * React Query hook for fetching AWS credentials status
 * Handles loading, error, and success states automatically
 */
export const useAwsCredentialsStatus = (options = {}) => {
  return useQuery({
    queryKey: ['aws-credentials-status'],
    queryFn: getAwsCredentialsStatus,
    staleTime: 20000, // 20 seconds
    cacheTime: 1000 * 60 * 60, // 1 hour
    refetchIntervalInBackground: true,
    keepPreviousData: true,
    ...options,
    select: (data) => ({
      ...data,
      hasStep1Complete: !!(
        data.s3Arn &&
        data.kmsKeyArn &&
        data.snsTopicArn &&
        data.lastAssumeRoleStatus === 'successful'
      ),
    }),
  });
};
